import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Regions from "@/pages/Regions";
import RegionDetail from "@/pages/RegionDetail";
import CountryDetail from "@/pages/CountryDetail";
import LanguageProjectDetail from "@/pages/LanguageProjectDetail";
import Projects from "@/pages/Projects";
import Users from "@/pages/Users";
import Books from "@/pages/Books";
import Clusters from "@/pages/Clusters";
import KairosSync from "@/pages/KairosSync";
import LanguagesPage from "@/pages/Languages";
import AuditLog from "@/pages/AuditLog";
import AnnotationAudit from "@/pages/AnnotationAudit";
import Notifications from "@/pages/Notifications";
import LanguageDetail from "@/pages/LanguageDetail";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Route users with a restricted menu (e.g., only "languages") to the first
 * menu they actually have access to instead of the default Overview.
 */
function DefaultLanding() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin") return <Overview />;
  const perms = user.menu_permissions || [];
  // Prefer overview if allowed; otherwise send them to the first allowed screen
  if (perms.includes("overview")) return <Overview />;
  const firstAllowed = ["languages", "projects", "regions", "clusters", "notifications"].find((k) => perms.includes(k));
  if (firstAllowed) return <Navigate to={`/${firstAllowed}`} replace />;
  return <Overview />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DefaultLanding />} />
              <Route path="regions" element={<Regions />} />
              <Route path="regions/:regionId" element={<RegionDetail />} />
              <Route path="countries/:countryId" element={<CountryDetail />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/language/:iso" element={<LanguageDetail />} />
              <Route path="projects/:projectId" element={<LanguageProjectDetail />} />
              <Route path="languages" element={<LanguagesPage />} />
              <Route path="clusters" element={<Clusters />} />
              <Route path="notifications" element={<Notifications />} />
              <Route
                path="users"
                element={<ProtectedRoute roles={["admin"]}><Users /></ProtectedRoute>}
              />
              <Route
                path="books"
                element={<ProtectedRoute roles={["admin"]}><Books /></ProtectedRoute>}
              />
              <Route
                path="kairos-sync"
                element={<ProtectedRoute roles={["admin"]}><KairosSync /></ProtectedRoute>}
              />
              <Route
                path="audit"
                element={<ProtectedRoute roles={["admin"]}><AuditLog /></ProtectedRoute>}
              />
              <Route
                path="audit/annotations"
                element={<ProtectedRoute roles={["admin"]}><AnnotationAudit /></ProtectedRoute>}
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
